import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import React from "react";

import { Icon } from "../../components/atoms/icon";
import styles from "./EventDescription.module.scss";
import { EventDetail } from "../../types";
import { Tag } from "../../components/atoms/tag";
import { ScheduledEventDetailFragment } from "../../generated/graphql";

interface Props {
  e2e?: string;
  event: EventDetail;
  eventIsFull: boolean;
  prefectureNames: string[];
  lotteryRejected: boolean;
}
interface State {
  hasMore: boolean;
  readMore: boolean;
}
const firstStartAt = (scheduledEvent: ScheduledEventDetailFragment): Dayjs => {
  const xs = scheduledEvent.dates
    .map((date) => dayjs(date.startAt))
    .sort((startAt1, startAt2) => startAt1.diff(startAt2));

  return xs[0];
};

const lastEndAt = (scheduledEvent: ScheduledEventDetailFragment): Dayjs => {
  const xs = scheduledEvent.dates
    .map((date) => dayjs(date.endAt))
    .sort((endAt1, endAt2) => endAt1.diff(endAt2));

  return xs[xs.length - 1];
};

class EventDescription extends React.PureComponent<Props, State> {
  companiesRef: React.RefObject<HTMLParagraphElement>;
  constructor(props: Props) {
    super(props);
    this.companiesRef = React.createRef();
    this.state = { hasMore: false, readMore: false };
  }

  public componentDidMount(): void {
    const element = this.companiesRef.current;
    if (element) this.readMoreCompanies(element);
  }

  private readMoreCompanies(el: HTMLParagraphElement) {
    if (el?.offsetHeight > 62) {
      this.setState({ hasMore: true });
    }
  }

  public render() {
    const {
      e2e,
      event,
      eventIsFull,
      prefectureNames,
      lotteryRejected,
    } = this.props;

    return (
      <div className={styles.container}>
        <ScheduledEventTagsMobile
          event={event}
          lotteryRejected={lotteryRejected}
          eventIsFull={eventIsFull}
          prefectureNames={prefectureNames}
        />
        <div className={styles.mobileReverseOrder}>
          <div className={styles.top}>
            {event.entryEvent ? (
              <div className={styles.iconTextContainer}>
                <div className={styles.iconText}>
                  <Icon name="calendar" size="small" />
                  <p className={styles.caption}>
                    申込締切：
                    {dayjs(event.entryEvent.deadline).format(
                      "YYYY/MM/DD(dd) HH:mm",
                    )}
                  </p>
                </div>
              </div>
            ) : null}
            {event.scheduledEvent ? (
              <div className={styles.iconTextContainer}>
                <div className={styles.iconText}>
                  <Icon name="calendar" size="small" />
                  <p className={styles.caption}>
                    {firstStartAt(event.scheduledEvent).format(
                      "YYYY/MM/DD(dd) HH:mm",
                    )}
                  </p>
                  {" ~ "}
                  <p className={styles.caption}>
                    {lastEndAt(event.scheduledEvent).format(
                      "YYYY/MM/DD(dd) HH:mm",
                    )}
                  </p>
                </div>
                <div
                  className={classNames(styles.iconText, styles.regions)}
                  tabIndex={-1}
                >
                  <Icon name="map_pin" />
                  <span className={styles.caption}>
                    {prefectureNames.slice(0, 5).join("、")}
                  </span>
                  {prefectureNames.length > 5 && (
                    <div className={styles.caption}>...</div>
                  )}
                </div>
              </div>
            ) : null}
            <div className={styles.tagsContainerDesktop}>
              <div className={styles.tagDesktop}>
                <ScheduledEventTags
                  lotteryRejected={lotteryRejected}
                  eventIsFull={eventIsFull}
                  event={event}
                  prefectureNames={prefectureNames}
                />
                <Tag color="green">
                  {event.category.name}
                  {event.isLotteryEvent && "(抽選有り)"}
                </Tag>
              </div>
            </div>
          </div>
          <div className={styles.title} {...(e2e ? { "data-e2e": e2e } : {})}>
            {event.title}
          </div>
        </div>

        <div className={styles.bottom}>
          <div className={styles.companyContainer} ref={this.companiesRef}>
            <span
              className={styles.companyName}
              data-hasMore={this.state.hasMore}
              data-readMore={this.state.readMore}
            >
              {event.clients.map((client) => client.company.name).join(", ")}
            </span>
            {this.state.hasMore && (
              <span
                className={styles.readMore}
                onClick={() =>
                  this.setState({ readMore: !this.state.readMore })
                }
              >
                {!this.state.readMore ? "すべて表示" : "閉じる"}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }
}

function ScheduledEventTags({ lotteryRejected, eventIsFull }: Props) {
  if (lotteryRejected) return <Tag color="lightGray">落選</Tag>;
  if (eventIsFull) return <Tag color="error">満席</Tag>;
  return <Tag color="accent">空席あり</Tag>;
}

function ScheduledEventTagsMobile({
  event,
  lotteryRejected,
  eventIsFull,
}: Props) {
  return (
    <div className={styles.tagsContainerMobile}>
      <div className={styles.tagMobile}>
        <Tag color="green">
          {event.category.name}
          {event.isLotteryEvent && "(抽選有り)"}
        </Tag>
      </div>
      {event.eventType === "scheduled" || event.isLotteryEvent ? (
        <div className={styles.vacancy}>
          {lotteryRejected ? (
            <Tag color="lightGray">落選</Tag>
          ) : eventIsFull ? (
            <Tag color="error">満席</Tag>
          ) : (
            <Tag color="accent">空席あり</Tag>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default EventDescription;
